import { useState, useEffect, useCallback, useRef } from 'react';

import { FormControlLabel, Switch } from '@mui/material';

import {
  Accordion,
  ConfirmationDialog,
  InputLabel,
  Stack,
  Typography,
} from '@liscio/ui';

import { useTaxDeliverySettings } from 'modules/requests/hooks/useTaxDeliverySettings';

export function AdvancedOptions() {
  const {
    kba: defaultKba,
    isLoading,
    updateKba,
  } = useTaxDeliverySettings();

  const [localKba, setLocalKba] = useState(defaultKba);
  const [pendingKba, setPendingKba] = useState<boolean | null>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!isLoading && !initializedRef.current) {
      setLocalKba(defaultKba);
      initializedRef.current = true;
    }
  }, [defaultKba, isLoading]);

  const handleKbaToggle = useCallback(() => {
    const newValue = !localKba;
    setPendingKba(newValue);
  }, [localKba]);

  const handleConfirmSaveAsDefault = () => {
    if (pendingKba === null) return;
    setLocalKba(pendingKba);
    updateKba(pendingKba);
    setPendingKba(null);
  };

  const handleCancelDialog = () => {
    setPendingKba(null);
  };

  if (isLoading) return null;

  return (
    <>
      <Accordion
        summary={
          <InputLabel>ADVANCED OPTIONS</InputLabel>
        }
        sx={{
          '&:before': { display: 'none' },
          bgcolor: 'transparent',
        }}
        summaryProps={{ sx: { px: 0 } }}
        detailsProps={{ sx: { px: 0 } }}
      >
        <Stack gap={2}>
          {/* KBA Toggle */}
          <Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={localKba}
                  onChange={handleKbaToggle}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  Require KBA for E-Signatures
                </Typography>
              }
            />
            <Typography variant="caption" color="text.secondary">
              {localKba
                ? 'Signers will be required to verify their identity.'
                : 'KBA is not required for signers on this request.'}
            </Typography>
          </Stack>
        </Stack>
      </Accordion>

      <ConfirmationDialog
        open={pendingKba !== null}
        title="Save as Default"
        content={`This will ${pendingKba ? 'enable' : 'disable'} KBA as the default for all future tax deliveries.`}
        confirmLabel="Save"
        cancelLabel="Cancel"
        onConfirm={handleConfirmSaveAsDefault}
        onCancel={handleCancelDialog}
      />
    </>
  );
}

export default AdvancedOptions;
